import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import communities from '../data/quinn-communities.json';
import { clean, num, shortName, stagger } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { nearbySiblings } from '../utils/places';

const bedsOf = (v: unknown) => {
  const [a, c] = String(v || '').split(/\s*-\s*/).map(num).filter((x): x is number => x != null);
  return a == null ? '' : c == null || c === a ? `${a} bd` : `${a}–${c} bd`;
};

/** Up to four other Quinn communities within 100 miles, nearest first. */
export function NearbyCommunities() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const near = nearbySiblings(p, communities);

  if (!near.length) {
    if (!editing) return null;
    return (
      <section className="section section--surface nearby" id="quinn-nearby">
        <RichTextContent path="nearbycommunities.title" as="h2" className="h2" />
        <p className="section__note">Nearby communities show here when this community has coordinates and other Quinn communities sit within 100 miles.</p>
      </section>
    );
  }

  return (
    <section className="section section--surface nearby" id="quinn-nearby">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="nearbycommunities.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="nearbycommunities.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="nearbycommunities.lead" as="div" className="section__lead" />
      </div>
      <div className="pgrid--nearby grid grid-cols-[repeat(4,1fr)] gap-6 max-[1199px]:grid-cols-[repeat(3,1fr)] max-[1023px]:grid-cols-[repeat(2,1fr)] max-[767px]:flex max-[767px]:flex-col max-[767px]:gap-3">
        {near.map((s, k) => {
          const url = clean(s.property_website_url);
          const homes = num(s.property_total_units);
          const facts = bedsOf(s.property_bedrooms_range);
          const name = shortName(clean(s.property_name));
          return (
            <a
              className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-[--q-border] bg-[--q-paper] text-inherit no-underline [transition:transform_var(--q-motion-normal)_var(--q-ease-out),box-shadow_var(--q-motion-normal)_var(--q-ease-out)] hover:[box-shadow:var(--q-shadow-md)] hover:[transform:translateY(-2px)] focus-visible:outline-offset-[3px] focus-visible:[box-shadow:var(--q-shadow-md)] focus-visible:[outline:2px_solid_var(--q-primary-on-paper)] focus-visible:[transform:translateY(-2px)] [&:hover_.textlink_.btn\_\_arrow]:[transform:translateX(4px)] [&:hover_img]:[transform:scale(1.04)] max-[767px]:flex-row"
              href={url || '#'}
              target={url ? '_blank' : undefined}
              rel="noopener"
              key={String(s.multihub_property_id || k)}
              data-reveal
              style={stagger(k)}
            >
              <div className="relative aspect-[16/10] shrink-0 overflow-hidden border-b border-b-[--q-border] bg-[--q-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:[transition:transform_var(--q-motion-slow)_var(--q-ease-out)] max-[767px]:aspect-auto max-[767px]:w-[124px] max-[767px]:border-r max-[767px]:border-r-[--q-border] max-[767px]:[border-bottom:0] max-[767px]:[&>.pill]:px-2 max-[767px]:[&>.pill]:py-1">
                <VibesitesImage url={String(s.property_featured_image)} alt={name} sizes="(min-width: 1200px) 25vw, (min-width: 768px) 33vw, 124px" />
                <span className="pill pill--paper absolute bottom-3 left-3 max-[767px]:bottom-2 max-[767px]:left-2">~{Math.round(s.dist)} mi</span>
              </div>
              <div className="flex min-w-0 flex-auto flex-col gap-1.5 px-5 py-[18px] max-[767px]:gap-1 max-[767px]:px-3.5 max-[767px]:pb-2.5 max-[767px]:pt-3">
                <div className="label">{[clean(s.property_address_city), clean(s.property_address_state)].filter(Boolean).join(', ')}</div>
                <h3 className="m-0 font-heading text-[22px] font-normal leading-7 [overflow-wrap:anywhere] max-[767px]:text-[19px] max-[767px]:leading-6">{name}</h3>
                <p className="m-0 line-clamp-2 text-sm leading-5 text-[--q-muted] max-[767px]:text-[13px] max-[767px]:leading-[18px]">
                  {[clean(s.property_type), homes ? `${homes} homes` : null].filter(Boolean).join(' · ')}
                </p>
                {facts && <div className="text-[13px] leading-[18px] text-[--q-muted] max-[767px]:text-[12px] max-[767px]:leading-4">{facts}</div>}
                <div className="mt-auto flex flex-wrap gap-x-5 gap-y-1 pt-2.5 [&_.textlink]:inline-flex [&_.textlink]:min-h-6 [&_.textlink]:items-center max-[767px]:gap-x-4 max-[767px]:gap-y-0 max-[767px]:pt-0.5 max-[767px]:[&_.textlink]:min-h-11">
                  <span className="textlink">Visit Site <span className="btn__arrow">→</span></span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NearbyCommunities',
  displayName: 'Nearby Communities',
  tokens: {
    'nearbycommunities.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_company_name"></span> nearby</p>',
    },
    'nearbycommunities.title': { label: 'Title', type: 'richtext', defaultValue: '<p>More Quinn communities nearby.</p>' },
    'nearbycommunities.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue:
        '<p>Looking at a wider area? These are the <span data-type="field-token" data-field="property_company_name"></span> communities closest to <span data-type="field-token" data-field="property_name"></span>, nearest first.</p>',
    },
  },
};

(NearbyCommunities as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NearbyCommunities;
